import { type FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TPO_METRIC_PAGE } from '../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import DeletePlanModal from '../CalendarPage/components/DeletePlanModal';
import DeleteAllPromotionsModal from '../CalendarPage/components/DeleteAllPromotionsModal';
import {
    TPOGEN_METRICS_REPORT_SWITCHED,
    TPOGEN_PLAN_METRICS_VIEW_SWITCHED,
    TPOGEN_REPORT_KPIS_CHANGED,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';
import { useExportJobs } from '../../ReportExport/context/ExportJobsContext';
import MetricToolbar from './MetricToolbar';
import PlanMetricView from './PlanMetricView';
import PromoMetricView from './PromoMetricView';
import useAttributeOptions from './hooks/queries/useAttributeOptions';
import { resolvePlanMetricDimension, resolvePlanMetricKpis, resolvePromoMetricKpis } from './utils/resolveSubModeDefaults';
import type {
    AttributeOption,
    FiltersPayload,
    Granularity,
    KpiViewType,
    MetricType,
    ReportView,
    SubMode,
    TimePeriod,
} from './types';

const EMPTY_FILTERS: FiltersPayload = {};

interface MetricViewProps {
    planId: string;
    planName: string;
}

const MetricView: FC<MetricViewProps> = ({ planId, planName }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { currentPlanDetails, optimizedPlanId, showOptimizedView } = usePlanDetailsContext();
    const { kpiCatalog, promoKpiCatalog } = useCustomerConfig();
    const { metricPageConfig } = useTpoGenConfig();
    const { isExportPending } = useExportJobs();

    const dimensionFallback = metricPageConfig.planMetricDimensions[0]?.value ?? '';
    const kpiFallback = kpiCatalog[0]?.key ?? '';
    const promoKpiFallback = promoKpiCatalog[0]?.key ?? '';

    // Shared toolbar state
    const [reportView, setReportView] = useState<ReportView>(metricPageConfig.defaultReportView);
    const [subMode, setSubMode] = useState<SubMode>(metricPageConfig.defaultSubMode);
    const [granularity, setGranularity] = useState<Granularity>(metricPageConfig.defaultGranularity);
    const [timePeriod, setTimePeriod] = useState<TimePeriod>(metricPageConfig.defaultTimePeriod);
    const [searchKeyword, setSearchKeyword] = useState<string>('');

    // Plan Metric state -- KPI is split into single (dimension sub-mode) and multi (metric sub-mode)
    // so each sub-mode keeps its own last-applied selection across switches.
    const [planDimension, setPlanDimension] = useState<string>(() =>
        resolvePlanMetricDimension(metricPageConfig.defaultPlanMetricDimension, metricPageConfig.defaultSubMode, dimensionFallback),
    );
    const [planDimensionKpi, setPlanDimensionKpi] = useState<string>(() =>
        resolvePlanMetricKpis(metricPageConfig.defaultPlanMetricKpis, 'dimension', kpiCatalog, kpiFallback)[0],
    );
    const [planMetricKpis, setPlanMetricKpis] = useState<string[]>(() =>
        resolvePlanMetricKpis(metricPageConfig.defaultPlanMetricKpis, 'metric', kpiCatalog, kpiFallback),
    );
    const [attributes, setAttributes] = useState<AttributeOption[]>([]);
    const [attributeSearch, setAttributeSearch] = useState<string>('');

    // Promo Metric state
    const [promoDimensions, setPromoDimensions] = useState<string[]>(
        () => [...metricPageConfig.defaultPromoMetricDimensions],
    );
    const [promoKpis, setPromoKpis] = useState<string[]>(() =>
        resolvePromoMetricKpis(metricPageConfig.defaultPromoMetricKpis, promoKpiCatalog, promoKpiFallback),
    );
    const [kpiViewType, setKpiViewType] = useState<KpiViewType>(metricPageConfig.defaultKpiViewType);
    const [metricType, setMetricType] = useState<MetricType>(metricPageConfig.defaultMetricType);

    // Plan-action modals (reused from Calendar)
    const [showDeletePlan, setShowDeletePlan] = useState(false);
    const [showDeleteAllPromotions, setShowDeleteAllPromotions] = useState(false);
    // The export modal is opened from the shared toolbar but rendered by whichever
    // sub-view is active, since only that view can compose its own payload.
    const [showExportModal, setShowExportModal] = useState(false);

    // Trend chart toggle (only valid for Plan Metrics + dimension sub-mode)
    const [showTrendChart, setShowTrendChart] = useState(false);

    useEffect(() => {
        if (reportView !== 'plan_metrics' || subMode !== 'dimension') {
            setShowTrendChart(false);
        }
    }, [reportView, subMode]);

    useEffect(() => {
        setPlanDimension(
            resolvePlanMetricDimension(metricPageConfig.defaultPlanMetricDimension, subMode, dimensionFallback),
        );
        setAttributes([]);
        setAttributeSearch('');
    }, [subMode, metricPageConfig.defaultPlanMetricDimension, dimensionFallback]);

    // No saved-metrics-report feature exists yet (see TPOGEN-075..079 gap), so
    // `report_id` is omitted and `report_name` is derived from the current
    // view/sub-mode combination as the closest available fe-derived label.
    const handleReportViewChange = useCallback(
        (next: ReportView) => {
            trackTpoGenEvent(TPOGEN_METRICS_REPORT_SWITCHED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_METRICS, {
                report_name: next,
                change_type: 'switch_plan_vs_promo',
            });
            setReportView(next);
        },
        [],
    );

    const handleSubModeChange = useCallback(
        (next: SubMode) => {
            trackTpoGenEvent(TPOGEN_PLAN_METRICS_VIEW_SWITCHED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_METRICS, {
                report_name: `plan_metrics:${next}`,
                change_type: 'switch_metrics_vs_dimension',
            });
            setSubMode(next);
        },
        [],
    );

    const handleShowExportModal = useCallback(() => setShowExportModal(true), []);
    const handleCloseExportModal = useCallback(() => setShowExportModal(false), []);

    const trackKpisChanged = useCallback((reportName: string) => {
        trackTpoGenEvent(TPOGEN_REPORT_KPIS_CHANGED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_METRICS, {
            report_name: reportName,
            change_type: 'change_kpi',
        });
    }, []);

    const handlePlanDimensionKpiChange = useCallback(
        (next: string) => {
            trackKpisChanged('plan_metrics:dimension');
            setPlanDimensionKpi(next);
        },
        [trackKpisChanged],
    );

    const handlePlanMetricKpisChange = useCallback(
        (next: string[]) => {
            trackKpisChanged('plan_metrics:metric');
            setPlanMetricKpis(next);
        },
        [trackKpisChanged],
    );

    const handlePromoKpisChange = useCallback(
        (next: string[]) => {
            trackKpisChanged('promo_metrics');
            setPromoKpis(next);
        },
        [trackKpisChanged],
    );

    const activePlanId = showOptimizedView && optimizedPlanId ? optimizedPlanId : planId;

    const enableAttributeFetch = reportView === 'plan_metrics' && subMode === 'metric';
    const attributeQuery = useAttributeOptions(
        activePlanId,
        enableAttributeFetch ? planDimension : null,
        EMPTY_FILTERS,
        attributeSearch,
    );

    const planKpis = subMode === 'dimension' ? [planDimensionKpi] : planMetricKpis;

    return (
        <div data-testid={TPO_METRIC_PAGE}>
            <MetricToolbar
                planId={planId}
                reportView={reportView}
                setReportView={handleReportViewChange}
                subMode={subMode}
                setSubMode={handleSubModeChange}
                timePeriod={timePeriod}
                setTimePeriod={setTimePeriod}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                planDimension={planDimension}
                setPlanDimension={setPlanDimension}
                planDimensionKpi={planDimensionKpi}
                setPlanDimensionKpi={handlePlanDimensionKpiChange}
                planMetricKpis={planMetricKpis}
                setPlanMetricKpis={handlePlanMetricKpisChange}
                attributes={attributes}
                setAttributes={setAttributes}
                attributeOptions={attributeQuery.options}
                attributeSearch={attributeSearch}
                setAttributeSearch={setAttributeSearch}
                attributesLoading={attributeQuery.isPending || attributeQuery.isFetching}
                onAttributeDropdownScroll={
                    attributeQuery.hasNextPage ? attributeQuery.fetchNextPage : undefined
                }
                promoDimensions={promoDimensions}
                setPromoDimensions={setPromoDimensions}
                promoKpis={promoKpis}
                setPromoKpis={handlePromoKpisChange}
                onShowDeletePlan={() => setShowDeletePlan(true)}
                onShowDeleteAllPromotions={() => setShowDeleteAllPromotions(true)}
                onShowExportModal={handleShowExportModal}
                isExportPending={isExportPending(
                    activePlanId,
                    reportView === 'plan_metrics' ? 'plan_metric' : 'promo_metric',
                )}
                showTrendChart={showTrendChart}
                onToggleTrendChart={() => setShowTrendChart((v) => !v)}
            />

            {reportView === 'plan_metrics' ? (
                <PlanMetricView
                    planId={activePlanId}
                    subMode={subMode}
                    dimension={planDimension}
                    attributes={attributes}
                    kpis={planKpis}
                    granularity={granularity}
                    setGranularity={setGranularity}
                    timePeriod={timePeriod}
                    searchKeyword={searchKeyword}
                    showTrendChart={showTrendChart}
                    showExportModal={showExportModal}
                    onCloseExportModal={handleCloseExportModal}
                />
            ) : (
                <PromoMetricView
                    planId={activePlanId}
                    dimensions={promoDimensions}
                    kpis={promoKpis}
                    timePeriod={timePeriod}
                    searchKeyword={searchKeyword}
                    kpiViewType={kpiViewType}
                    setKpiViewType={setKpiViewType}
                    metricType={metricType}
                    setMetricType={setMetricType}
                    showExportModal={showExportModal}
                    onCloseExportModal={handleCloseExportModal}
                />
            )}

            <DeletePlanModal
                open={showDeletePlan}
                onClose={() => setShowDeletePlan(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName}
                onSuccess={() => navigate(`/tpo-gen/plans-index${state?.fromSearch ?? ''}`)}
            />
            <DeleteAllPromotionsModal
                open={showDeleteAllPromotions}
                onClose={() => setShowDeleteAllPromotions(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName || planId}
            />
        </div>
    );
};

MetricView.displayName = 'MetricView';
export default MetricView;
